// =============================================================================
// Phase 2: Range Detector (Slim Version)
// Detects explicit range separators and splits input into from/to parts
// Delegates implicit range parsing to naturalLanguage.ts
// =============================================================================

import { WORD_SEPARATORS, SYMBOL_SEPARATORS } from "./utils/constants";
import { parseNaturalLanguage } from "./naturalLanguage";

export interface RangeResult {
  isRange: boolean;
  from?: string;
  to?: string;
  value?: string;
}

export interface DetectRangeOptions {
  pickerType?: "single" | "range";
  granularity?: "second" | "minute" | "hour" | "day";
}

// -----------------------------------------------------------------------------
// Input Normalization
// -----------------------------------------------------------------------------

/**
 * Normalize input: handle unicode whitespace, collapse spaces, lowercase
 */
function normalizeInput(input: string): string {
  return input
    .replace(/[\u00A0\t\n\r\f\v]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Normalize a range value - lowercase text but preserve expression case
 */
function normalizeRangeValue(value: string): string {
  // If it's a Grafana-style expression (starts with "now"), preserve case for units
  if (/^now/i.test(value)) {
    return value.replace(/^now/i, "now");
  }
  // For ISO dates with T, preserve the T
  if (/^\d{4}-\d{2}-\d{2}T/i.test(value)) {
    return value.replace(/T/gi, "T").toLowerCase().replace(/t/g, "T");
  }
  // For everything else, lowercase
  return value.toLowerCase();
}

// -----------------------------------------------------------------------------
// Separator Detection
// -----------------------------------------------------------------------------

/**
 * Find explicit separator in input - returns separator and its position
 */
function findExplicitSeparator(input: string): { separator: string; index: number; length: number } | null {
  const lower = input.toLowerCase();

  // Check symbol separators first (longer ones first)
  for (const sep of SYMBOL_SEPARATORS) {
    const idx = lower.indexOf(sep);
    if (idx > 0 && idx < lower.length - sep.length) {
      return { separator: sep, index: idx, length: sep.length };
    }
  }

  // Check word separators with word boundaries
  for (const sep of WORD_SEPARATORS) {
    const regex = new RegExp(`\\s+${sep}\\s+`, "i");
    const match = lower.match(regex);
    if (match && match.index !== undefined) {
      return { separator: sep, index: match.index, length: match[0].length };
    }
  }

  // Check for " - " (dash with spaces) as separator
  // But not if it's part of an expression like "now-7d - now"
  const dashMatch = lower.match(/\s+-\s+/);
  if (dashMatch && dashMatch.index !== undefined) {
    // Make sure it's not followed by a number+unit pattern
    const afterDash = lower.slice(dashMatch.index + dashMatch[0].length);
    const isExpression = /^\d+[smhdwMy]/i.test(afterDash);
    if (!isExpression) {
      return { separator: "-", index: dashMatch.index, length: dashMatch[0].length };
    }
  }

  // Check for "between...and" pattern
  if (lower.startsWith("between ")) {
    const andMatch = lower.match(/\s+and\s+/);
    if (andMatch && andMatch.index !== undefined) {
      return { separator: "and", index: andMatch.index, length: andMatch[0].length };
    }
  }

  return null;
}

// -----------------------------------------------------------------------------
// Main Detection Function
// -----------------------------------------------------------------------------

/**
 * Detect if input contains an explicit range separator and split into parts.
 * This function ONLY handles explicit separators (to, ->, .., ~, etc.)
 * Implicit ranges (last 7 days, this week, Q1 2026) are handled by naturalLanguage.ts
 */
export function detectRange(input: string, options: DetectRangeOptions = {}): RangeResult {
  const normalized = normalizeInput(input);
  const trimmedInput = input.replace(/[\u00A0\t\n\r\f\v]+/g, " ").replace(/\s+/g, " ").trim();

  // Handle empty input
  if (!normalized) {
    return { isRange: false };
  }

  // Handle single separator words (invalid as standalone input)
  if (WORD_SEPARATORS.includes(normalized) || ["from", "between"].includes(normalized)) {
    return { isRange: false };
  }

  // Strip "from " prefix if present (for "from X to Y" pattern)
  let processedInput = trimmedInput;
  let processedNormalized = normalized;
  if (processedNormalized.startsWith("from ")) {
    processedInput = processedInput.slice(5);
    processedNormalized = processedNormalized.slice(5);
  }

  // Check for explicit range separator
  const separatorInfo = findExplicitSeparator(processedInput);

  if (separatorInfo) {
    const { separator, index, length } = separatorInfo;

    // Extract from/to using the original case input
    let from = processedInput.slice(0, index).trim();
    let to = processedInput.slice(index + length).trim();

    // Handle "between X and Y" - strip "between " from "from"
    if (separator === "and" && processedNormalized.startsWith("between ")) {
      from = from.replace(/^between\s+/i, "");
    }

    // Validate both parts exist
    if (from && to) {
      return {
        isRange: true,
        from: normalizeRangeValue(from),
        to: normalizeRangeValue(to),
      };
    }

    // Incomplete range
    return { isRange: false };
  }

  // No explicit separator found - try implicit range patterns via naturalLanguage
  // Always pass "range" since detectRange is specifically looking for ranges
  // Exception: single day keywords (today/yesterday/tomorrow) should NOT be ranges in single picker mode
  const singleDayKeywords = ["today", "yesterday", "tomorrow"];
  if (singleDayKeywords.includes(normalized) && options.pickerType !== "range") {
    return { isRange: false, value: input.trim() };
  }

  const nlResult = parseNaturalLanguage(normalized, { pickerType: "range" });

  if (nlResult && nlResult.type === "range" && nlResult.from && nlResult.to) {
    return {
      isRange: true,
      from: nlResult.from,
      to: nlResult.to,
    };
  }

  // Try year-only as implicit range (2020-2030 range)
  // This runs after natural language, so time-like values can fall through to time parsing
  const yearMatch = normalized.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    if (year >= 2020 && year <= 2030) {
      return {
        isRange: true,
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      };
    }
  }

  // Not a range
  return { isRange: false, value: input.trim() };
}
